/**
 * ResourceEntryForm - Modal for adding resource readings
 *
 * Supports:
 * - Single reading entry
 * - Bulk inspection entry (all resources at once)
 * - Source selection (manual, inspection, maintenance)
 */
import React, { useState, useEffect } from 'react';
import { X, Save, Gauge, Wrench, ClipboardCheck, Calendar, Fuel, Droplet, Circle } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

// All resource types
const RESOURCE_TYPES = {
  // Fluids
  fuel_level: { label: 'Carburant', icon: Fuel, group: 'fluids', unit: '%' },
  adblue_level: { label: 'AdBlue', icon: Droplet, group: 'fluids', unit: '%', dieselOnly: true },
  oil_level: { label: 'Huile moteur', icon: Droplet, group: 'fluids', unit: '%' },
  brake_fluid: { label: 'Liquide de frein', icon: Droplet, group: 'fluids', unit: '%' },
  coolant_level: { label: 'Liquide refroidissement', icon: Droplet, group: 'fluids', unit: '%' },
  washer_fluid: { label: 'Lave-glace', icon: Droplet, group: 'fluids', unit: '%' },
  // Wear items
  brake_pad_front: { label: 'Plaquettes frein AV', icon: Circle, group: 'wear', unit: '%' },
  brake_pad_rear: { label: 'Plaquettes frein AR', icon: Circle, group: 'wear', unit: '%' },
  tire_front_left: { label: 'Pneu avant gauche', icon: Circle, group: 'wear', unit: '%' },
  tire_front_right: { label: 'Pneu avant droit', icon: Circle, group: 'wear', unit: '%' },
  tire_rear_left: { label: 'Pneu arrière gauche', icon: Circle, group: 'wear', unit: '%' },
  tire_rear_right: { label: 'Pneu arrière droit', icon: Circle, group: 'wear', unit: '%' },
  // Hull
  windshield:     { label: 'Pare-brise',    icon: Circle, group: 'hull', unit: '%' },
  body_condition: { label: 'Carrosserie',   icon: Circle, group: 'hull', unit: '%' },
  headlights:     { label: 'Eclairage AV',  icon: Circle, group: 'hull', unit: '%' },
  taillights:     { label: 'Eclairage AR',  icon: Circle, group: 'hull', unit: '%' },
  mirrors:        { label: 'Retroviseurs',  icon: Circle, group: 'hull', unit: '%' },
};

// Source options
const SOURCES = [
  { value: 'manual', label: 'Saisie manuelle', icon: Gauge },
  { value: 'inspection', label: 'Inspection', icon: ClipboardCheck },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench },
];

export function ResourceEntryForm({
  isOpen,
  onClose,
  onSubmit,
  vehicleId,
  vehicleFuelType,
  vehicleMileage = 0,
  mode = 'single', // 'single' or 'bulk'
  preselectedType = null,
}) {
  const { fmt } = useFormat();
  const [formData, setFormData] = useState({
    resource_type: preselectedType || '',
    value_percent: '',
    source: 'manual',
    reading_date: new Date().toISOString().split('T')[0],
    odometer_km: vehicleMileage ? String(vehicleMileage) : '',
    notes: '',
  });

  // For bulk mode
  const [bulkReadings, setBulkReadings] = useState(
    Object.keys(RESOURCE_TYPES).reduce((acc, type) => {
      const config = RESOURCE_TYPES[type];
      if (config.dieselOnly && vehicleFuelType !== 'diesel') return acc;
      acc[type] = '';
      return acc;
    }, {})
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDiesel = vehicleFuelType === 'diesel';

  // Resync form state when modal (re)opens — useState init runs only once,
  // so preselectedType / vehicleMileage changes after mount were ignored.
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      resource_type: preselectedType || '',
      value_percent: '',
      source: 'manual',
      reading_date: new Date().toISOString().split('T')[0],
      odometer_km: vehicleMileage ? String(vehicleMileage) : '',
      notes: '',
    });
    setError(null);
  }, [isOpen, preselectedType, vehicleMileage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate odometer: prevent decrease
      if (formData.odometer_km && vehicleMileage > 0 && parseInt(formData.odometer_km) < vehicleMileage) {
        setError(`Le kilométrage ne peut pas être inférieur à ${fmt.number(vehicleMileage)} km`);
        setLoading(false);
        return;
      }

      if (mode === 'bulk') {
        // Filter out empty readings
        const readings = Object.entries(bulkReadings)
          .filter(([_, value]) => value !== '' && value !== null)
          .map(([type, value]) => ({
            resource_type: type,
            value_percent: parseFloat(value),
            source: formData.source,
            reading_date: formData.reading_date,
            notes: formData.notes,
          }));

        if (readings.length === 0) {
          setError('Veuillez saisir au moins une valeur');
          setLoading(false);
          return;
        }

        await onSubmit({
          readings,
          odometer_km: formData.odometer_km ? parseInt(formData.odometer_km) : null,
        });
      } else {
        // Single reading
        if (!formData.resource_type || !formData.value_percent) {
          setError('Veuillez remplir tous les champs obligatoires');
          setLoading(false);
          return;
        }

        await onSubmit({
          resource_type: formData.resource_type,
          value_percent: parseFloat(formData.value_percent),
          source: formData.source,
          reading_date: formData.reading_date,
          odometer_km: formData.odometer_km ? parseInt(formData.odometer_km) : null,
          notes: formData.notes || null,
        });
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Gauge size={20} className="text-blue-600" />
            {mode === 'bulk' ? 'Inspection complète' : preselectedType && RESOURCE_TYPES[preselectedType] ? `Mesure — ${RESOURCE_TYPES[preselectedType].label}` : 'Nouvelle mesure'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-130px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar size={14} className="inline mr-1" />
                Date
              </label>
              <input
                type="date"
                value={formData.reading_date}
                onChange={(e) => setFormData({ ...formData, reading_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Odometer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kilométrage
              </label>
              <input
                type="number"
                value={formData.odometer_km}
                onChange={(e) => setFormData({ ...formData, odometer_km: e.target.value })}
                placeholder={vehicleMileage > 0 ? String(vehicleMileage) : 'ex: 45000'}
                min={vehicleMileage || 0}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  formData.odometer_km && vehicleMileage > 0 && parseInt(formData.odometer_km) < vehicleMileage
                    ? 'border-red-400 bg-red-50' : 'border-gray-300'
                }`}
              />
              {vehicleMileage > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">Actuel : {fmt.number(vehicleMileage)} km</p>
              )}
              {formData.odometer_km && vehicleMileage > 0 && parseInt(formData.odometer_km) < vehicleMileage && (
                <p className="text-xs text-red-500 mt-0.5">Le kilométrage ne peut pas diminuer</p>
              )}
            </div>
          </div>

          {/* Source selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
            <div className="flex gap-2">
              {SOURCES.map((source) => {
                const Icon = source.icon;
                return (
                  <button
                    key={source.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, source: source.value })}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      formData.source === source.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="text-sm">{source.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {mode === 'single' ? (
            /* Single reading mode */
            <>
              {/* Resource type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de ressource *
                </label>
                <select
                  value={formData.resource_type}
                  onChange={(e) => setFormData({ ...formData, resource_type: e.target.value })}
                  disabled={!!preselectedType}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${preselectedType ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <optgroup label="Niveaux">
                    {Object.entries(RESOURCE_TYPES)
                      .filter(([_, c]) => c.group === 'fluids' && (!c.dieselOnly || isDiesel))
                      .map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                  </optgroup>
                  <optgroup label="Usure">
                    {Object.entries(RESOURCE_TYPES)
                      .filter(([_, c]) => c.group === 'wear')
                      .map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                  </optgroup>
                  <optgroup label="Carrosserie">
                    {Object.entries(RESOURCE_TYPES)
                      .filter(([_, c]) => c.group === 'hull')
                      .map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                  </optgroup>
                </select>
              </div>

              {/* Value */}
              <div className="mb-4">
                {formData.resource_type && RESOURCE_TYPES[formData.resource_type]?.group === 'wear' ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vie restante (%) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={formData.value_percent}
                      onChange={(e) => setFormData({ ...formData, value_percent: e.target.value })}
                      placeholder="ex: 80"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">100% = neuf, 0% = à remplacer</p>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valeur (%) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={formData.value_percent}
                      onChange={(e) => setFormData({ ...formData, value_percent: e.target.value })}
                      placeholder="ex: 75"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    {formData.resource_type && <p className="text-xs text-gray-400 mt-1">0% = vide, 100% = plein</p>}
                  </>
                )}
              </div>
            </>
          ) : (
            /* Bulk mode */
            <>
              {/* Fluids section */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <Droplet size={16} className="text-blue-500" />
                  Niveaux
                </h4>
                <p className="text-xs text-gray-400 mb-2">0% = vide, 100% = plein</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(RESOURCE_TYPES)
                    .filter(([_, c]) => c.group === 'fluids' && (!c.dieselOnly || isDiesel))
                    .map(([key, config]) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-600 mb-1">{config.label}</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={bulkReadings[key] || ''}
                            onChange={(e) => setBulkReadings({ ...bulkReadings, [key]: e.target.value })}
                            placeholder="-"
                            className="w-full px-3 py-1.5 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Wear items section */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <Circle size={16} className="text-gray-500" />
                  Usure — Vie restante
                </h4>
                <p className="text-xs text-gray-400 mb-2">100% = neuf, 0% = à remplacer</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(RESOURCE_TYPES)
                    .filter(([_, c]) => c.group === 'wear')
                    .map(([key, config]) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-600 mb-1">{config.label}</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={bulkReadings[key] || ''}
                            onChange={(e) => setBulkReadings({ ...bulkReadings, [key]: e.target.value })}
                            placeholder="-"
                            className="w-full px-3 py-1.5 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Hull section */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <Circle size={16} className="text-amber-500" />
                  Carrosserie
                </h4>
                <p className="text-xs text-gray-400 mb-2">100% = parfait, 0% = a remplacer</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(RESOURCE_TYPES)
                    .filter(([_, c]) => c.group === 'hull')
                    .map(([key, config]) => (
                      <div key={key}>
                        <label className="block text-xs text-gray-600 mb-1">{config.label}</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={bulkReadings[key] || ''}
                            onChange={(e) => setBulkReadings({ ...bulkReadings, [key]: e.target.value })}
                            placeholder="-"
                            className="w-full px-3 py-1.5 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observations éventuelles..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 py-3 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} />
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResourceEntryForm;
